import React, { useCallback, useState } from "react";
import { Button, Col, Container, Row } from "reactstrap";
import { useIntl } from "react-intl";
import FieldForm from "../creator/FieldForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InternalModal from "../../../layout/InternalModal";
import { CUSTOM_FIELD } from "../../../../constants/internalModal.constant";
import { useGlobalCore } from "../../../../cores/globals";
import { CustomField } from "../../../../cores/boards/interfaces/api/get-custom-fields";
import { useBoardCore } from "../../../../cores/boards";
import { useMeCore } from "../../../../cores/me";
import {
  CardCustomField,
  CustomFieldTemplate,
  CustomFieldTemplates,
} from "../../../../cores/boards/constants/custom-field-type";

interface Props {
  board: string;
}

export default function CustomFields({ board }: Props) {
  const intl = useIntl();
  const { company } = useMeCore();
  const { customFields, postCustomField } = useBoardCore();
  const {
    turnOnInternalModal,
    turnOffInternalModal,
    isInternalModal,
  } = useGlobalCore();
  const [field, setField] = useState<CustomField | null>(null);
  const [creatingTemplate, setCreatingTemplate] = useState<string | null>(null);

  const editTag = useCallback(
    (field: CustomField) => {
      turnOnInternalModal(CUSTOM_FIELD, true);
      setField(field);
    },
    [turnOnInternalModal]
  );

  const createTag = useCallback(() => {
    turnOnInternalModal(CUSTOM_FIELD, true);
    setField(null);
  }, [turnOnInternalModal]);

  const createFromTemplate = useCallback(
    async (template: CustomFieldTemplate) => {
      setCreatingTemplate(template.type);
      try {
        await postCustomField(company?.id!, board, {
          name: intl.formatMessage({ id: template.nameKey }),
          type: template.type,
        });
      } finally {
        setCreatingTemplate(null);
      }
    },
    [postCustomField, company, board, intl]
  );

  const getFieldTypeLabel = useCallback(
    (type: string) => {
      const labels: Record<string, string> = {
        [CardCustomField.Text]: "app.opportunity.customField.text",
        [CardCustomField.Number]: "app.opportunity.customField.number",
        [CardCustomField.Date]: "app.opportunity.customField.date",
        [CardCustomField.Money]: "app.opportunity.customField.money",
        [CardCustomField.Url]: "app.opportunity.customField.url",
        [CardCustomField.Email]: "app.opportunity.customField.email",
      };

      return intl.formatMessage({ id: labels[type] || labels[CardCustomField.Text] });
    },
    [intl]
  );

  return (
    <div className="position-relative h-100">
      <InternalModal
        active={isInternalModal(CUSTOM_FIELD)}
        onClose={() => turnOffInternalModal(CUSTOM_FIELD)}
      >
        <FieldForm field={field!} board={board} />
      </InternalModal>
      <Container className="h-100" fluid>
        <Row className="h-100">
          <Col xs="12">
            <div className="d-flex flex-column h-100 p-4">
              <h2 className="h2 text-center mb-4">
                {intl.formatMessage({ id: "app.contacts.customFields" })}
              </h2>

              <p className="text-muted text-center small mb-2">
                {intl.formatMessage({ id: "app.crmCustomField.templatesHint" })}
              </p>

              <div className="crm-custom-field-templates mb-3">
                {CustomFieldTemplates.map((template) => (
                  <button
                    key={template.type}
                    type="button"
                    className="crm-custom-field-template-btn"
                    disabled={creatingTemplate === template.type}
                    onClick={() => createFromTemplate(template)}
                  >
                    <FontAwesomeIcon
                      icon={["fas", template.icon]}
                      className="mr-2"
                    />
                    {intl.formatMessage({ id: template.nameKey })}
                  </button>
                ))}
              </div>

              <Button color="ligth" className="mb-3 border" onClick={createTag}>
                {intl.formatMessage({ id: "app.contacts.customFields.create.new" })}
              </Button>

              {customFields.length === 0 && (
                <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                  {intl.formatMessage({ id: "app.contacts.noCustomFields" })}
                </div>
              )}
              {customFields.map((field) => (
                <div
                  className="d-flex justify-content-between align-items-center my-1"
                  key={field.id}
                >
                  <div className="d-flex flex-column min-w-0">
                    <span className="truncate">{field.name}</span>
                    <span className="text-muted small">
                      {getFieldTypeLabel(field.type)}
                    </span>
                  </div>

                  <FontAwesomeIcon
                    icon={["fas", "edit"]}
                    size="1x"
                    className="ml-3 pointer flex-shrink-0"
                    color="gray"
                    onClick={() => editTag(field)}
                  />
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
